import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Text } from '@/components/common/text';
import { ICommunityResource } from '@/types/admin/resources';
import { CommunityResource } from '@/types/residents/communityResource';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ResourcesSkeleton } from './ResourcesSkeleton';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { ResourceGlassCard } from '@/components/shared/redesign';
import { ResourceIcon as AdminResourceIcon } from '@/components/admin/resources/common/ResourceIcon';
import { getResourceItemCount } from '@/components/admin/resources/common/resourceUtils';

type ResourceTile = {
  position: number;
  resource?: ICommunityResource;
  isPrimary: boolean;
};

interface ResourceGridProps {
  tiles: ResourceTile[];
  onResourcePress?: (resource: ICommunityResource) => void;
  onEditResource?: (resource: ICommunityResource) => void;
  onEmptySlotPress?: (position: number) => void;
  onCreateResource?: () => void;
  isEditable?: boolean;
  loading?: boolean;
  selectedResourceId?: number | null;
  onSelectResource?: (resourceId: number | null) => void;
  onUpdateResourcePositions?: (resourceId: number, position: number) => Promise<void> | void;
}

export const ResourceGrid: React.FC<ResourceGridProps> = React.memo(({
  tiles,
  onResourcePress,
  onEditResource,
  onEmptySlotPress,
  onCreateResource,
  isEditable = true,
  loading = false,
  selectedResourceId = null,
  onSelectResource,
  onUpdateResourcePositions,
}) => {
  const { redesign } = useAdminColors();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const primaryTiles = useMemo(() => tiles.filter(t => t.position < 4), [tiles]);
  const secondaryTiles = useMemo(() => tiles.filter(t => t.position >= 4), [tiles]);

  const renderEmptySlot = useCallback((position: number) => (
    <TouchableOpacity
      key={`empty-${position}`}
      style={[styles.gridItem, styles.emptySlot, { borderColor: redesign.card.border }]}
      onPress={() => (onEmptySlotPress ? onEmptySlotPress(position) : onCreateResource?.())}
      activeOpacity={0.7}
    >
      <View style={styles.emptySlotContent}>
        <SVG_COMPONENTS.ADD_BUTTON width={24} height={24} color={redesign.text.subtle} />
        <Text style={[styles.emptySlotText, { color: redesign.text.subtle }]}>
          {selectedResourceId != null ? 'Move here' : 'Add resource'}
        </Text>
      </View>
    </TouchableOpacity>
  ), [onEmptySlotPress, onCreateResource, selectedResourceId, redesign.card.border, redesign.text.subtle]);

  const renderTile = useCallback((tile: ResourceTile) => {
    if (tile.resource) {
      const resource = tile.resource;
      const totalItems = getResourceItemCount(resource);
      const isSelected = resource.id === selectedResourceId;
      const isDropTarget =
        selectedResourceId != null && selectedResourceId !== resource.id;

      const handleDropTargetPress = async () => {
        if (selectedResourceId == null || selectedResourceId === resource.id) return;
        if (typeof onUpdateResourcePositions !== 'function') return;
        try {
          await onUpdateResourcePositions(selectedResourceId, resource.position);
          showSuccess('Resource moved');
        } catch (err: any) {
          showApiError(err, 'Failed to move resource');
        } finally {
          onSelectResource?.(null);
        }
      };

      return (
        <ResourceGlassCard
          key={`res-${resource.id}`}
          resource={resource as unknown as CommunityResource}
          editMode
          showEditButton={isEditable}
          onPress={handleDropTargetPress}
          onEdit={onEditResource ? () => onEditResource(resource) : undefined}
          isSelected={isSelected}
          onToggleSelect={() => {
            if (onSelectResource) {
              onSelectResource(resource.id === selectedResourceId ? null : resource.id);
            }
          }}
          secondaryMeta={totalItems > 0 ? `${totalItems} items` : undefined}
          iconRenderer={(key) => (
            <AdminResourceIcon iconKey={key} size={18} color={redesign.text.primary} />
          )}
          style={styles.gridItem}
          minHeight={160}
          avatarSize={32}
          bodyPressable={isDropTarget}
        />
      );
    }
    return renderEmptySlot(tile.position);
  }, [onEditResource, isEditable, renderEmptySlot, onSelectResource, selectedResourceId, onUpdateResourcePositions, showSuccess, showApiError, redesign.text.primary]);

  const renderPrimaryGrid = () => {
    const gridItems: React.ReactNode[] = [];
    for (let i = 0; i < 4; i++) {
      const tile = primaryTiles.find(t => t.position === i) || { position: i } as ResourceTile;
      gridItems.push(renderTile(tile));
    }

    return (
      <View style={styles.primaryGrid}>
        <View style={styles.gridRow}>
          {gridItems[0]}
          {gridItems[1]}
        </View>
        <View style={styles.gridRow}>
          {gridItems[2]}
          {gridItems[3]}
        </View>
      </View>
    );
  };

  const renderSecondaryGrid = () => {
    if (secondaryTiles.length === 0) {
      return null;
    }
    const rows: React.ReactNode[] = [];
    for (let i = 0; i < secondaryTiles.length; i += 2) {
      const slice = secondaryTiles.slice(i, i + 2);
      rows.push(
        <View key={`row-${i}`} style={styles.gridRow}>
          {slice.map(tile => (
            renderTile(tile)
          ))}
        </View>
      );
    }
    return (
      <View style={styles.secondarySection}>
        <Text style={[styles.sectionTitle, { color: redesign.text.primary }]}>Other Resources</Text>
        {rows}
      </View>
    );
  };

  if (loading) {
    return <ResourcesSkeleton />;
  }

  const hasAnyResource = tiles.some(t => Boolean(t.resource));

  return (
    <View style={styles.container}>
      <View style={styles.primarySection}>
        <Text style={[styles.sectionTitle, { color: redesign.text.primary }]}>Residents Dashboard</Text>
        {renderPrimaryGrid()}
      </View>

      {renderSecondaryGrid()}

      {!hasAnyResource && (
        <TouchableOpacity
          style={[styles.addFirstResourceButton, { backgroundColor: redesign.card.bg, borderColor: redesign.card.border }]}
          onPress={onCreateResource}
          activeOpacity={0.7}
        >
          <SVG_COMPONENTS.ADD_BUTTON width={32} height={32} color={redesign.accent.blueBg} />
          <Text style={[styles.addFirstResourceText, { color: redesign.text.primary }]}>Create your first resource</Text>
          <Text style={[styles.addFirstResourceSubtext, { color: redesign.text.muted }]}>
            Resources help residents access important information and services
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
});

ResourceGrid.displayName = 'ResourceGrid';

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    fontSize: 16,
  },
  primarySection: {
    marginBottom: 32,
  },
  secondarySection: {
    flex: 1,
    gap: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
  },
  primaryGrid: {
    gap: 12,
  },
  gridRow: {
    flexDirection: 'row',
    gap: 12,
  },
  gridItem: {
    flexBasis: '48%',
    maxWidth: '48%',
    minWidth: 0,
    flexGrow: 0,
  },
  emptySlot: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderRadius: 12,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 160,
  },
  emptySlotContent: {
    alignItems: 'center',
    gap: 8,
  },
  emptySlotText: {
    fontSize: 12,
    fontWeight: '500',
  },
  addFirstResourceButton: {
    alignItems: 'center',
    padding: 40,
    borderRadius: 12,
    borderWidth: 2,
    borderStyle: 'dashed',
    marginTop: 40,
  },
  addFirstResourceText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  addFirstResourceSubtext: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
});
